import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Amount, Button, ExternalLink, KeyValue, LinkButton, Notice } from "../../components/ui";
import { IconAlert, IconClock, IconReceipt, IconShield } from "../../components/icons";
import { SuccessMark, useCountUp } from "../../components/motion";
import { useI18n } from "../../i18n";
import { useBrand } from "../../branding/BrandContext";
import { RedirectCountdown } from "../../components/RedirectCountdown";
import { chainLabel, explorerTxUrl } from "../../lib/chains";
import { formatDateTime, safeRedirect } from "../../lib/format";
import { diffUnits, formatDisplay, formatUnits, shortHash } from "../../lib/money";
import type { PublicInvoice } from "../../lib/types";

type FinalView = "paid" | "overpaid" | "underpaid" | "expired" | "flagged";

export function ResultView({ invoice, view, sawPayment = false }: { invoice: PublicInvoice; view: FinalView; sawPayment?: boolean }) {
  const { t, locale } = useI18n();
  const brand = useBrand();
  const merchant = invoice.merchantName;
  const asset = invoice.asset.symbol;
  const dec = invoice.asset.decimals;
  const redirect = safeRedirect(invoice.redirectUrl);
  const receiptHref = `/i/${encodeURIComponent(invoice.id)}`;
  const received = invoice.receivedAmount;

  const tone = view === "paid" || view === "overpaid" ? "success" : view === "expired" ? "neutral" : "warning";
  const icon =
    tone === "success" ? null : view === "expired" ? <IconClock size={28} /> : view === "flagged" ? <IconShield size={28} /> : <IconAlert size={28} />;
  const paidValue = formatUnits(received ?? invoice.amount, dec);
  // The paid figure counts up once, only when this page watched the payment arrive.
  const paidShown = useCountUp(paidValue, { from: sawPayment ? "0" : undefined, animate: sawPayment });

  let title: string;
  let body: ReactNode;
  switch (view) {
    case "paid":
      title = t("done.paidTitle");
      body = t("done.paidBody", { merchant });
      break;
    case "overpaid":
      title = t("done.overpaidTitle");
      body = t("done.overpaidBody", {
        extra: received ? formatDisplay(diffUnits(received, invoice.amount), dec, locale) : "—",
        asset,
        merchant,
      });
      break;
    case "underpaid":
      title = t("done.underpaidTitle");
      body = t("done.underpaidBody", {
        received: received ? formatDisplay(received, dec, locale) : "—",
        amount: formatDisplay(invoice.amount, dec, locale),
        missing: received ? formatDisplay(diffUnits(invoice.amount, received), dec, locale) : "—",
        asset,
      });
      break;
    case "expired":
      title = t("done.expiredTitle");
      body = t("done.expiredBody");
      break;
    case "flagged":
      title = t("done.flaggedTitle");
      body = t("done.flaggedBody", { merchant });
      break;
  }

  const settled = view === "paid" || view === "overpaid";
  const txHash = invoice.txHash;
  const explorer = txHash ? (invoice.explorerUrl ?? explorerTxUrl(invoice.asset.chain, txHash)) : null;

  return (
    <section className={`result result-${tone}`} aria-live="polite">
      <div className="result-head">
        {tone === "success" ? (
          <SuccessMark />
        ) : (
          <span className={`result-icon result-icon-${tone}`} aria-hidden="true">
            {icon}
          </span>
        )}
        <h2 className="h1">{title}</h2>
        <p className="result-body">{body}</p>
      </div>

      {view === "underpaid" ? <Notice tone="warning">{t("done.underpaidNext", { merchant })}</Notice> : null}
      {view === "expired" ? <Notice tone="info">{t("done.expiredLate", { merchant })}</Notice> : null}

      {view !== "expired" ? (
        <div className="receipt-card">
          <div className="receipt-card-title">{t("done.receipt")}</div>
          <dl className="kv-list">
            {view === "underpaid" ? (
              <>
                <KeyValue label={t("done.expected")}>
                  <Amount value={formatUnits(invoice.amount, dec)} symbol={asset} />
                </KeyValue>
                <KeyValue label={t("done.received")}>
                  {received ? <Amount value={formatUnits(received, dec)} symbol={asset} /> : "—"}
                </KeyValue>
              </>
            ) : (
              <KeyValue label={t("done.amountPaid")}>
                <span className="sr-only">
                  {formatDisplay(received ?? invoice.amount, dec, locale)} {asset}
                </span>
                <span aria-hidden="true">
                  <Amount value={paidShown} symbol={asset} className="strong" />
                </span>
              </KeyValue>
            )}
            {(settled || view === "underpaid") && invoice.paidAt ? (
              <KeyValue label={t("done.paidAt")}>
                <span className="num">{formatDateTime(invoice.paidAt, locale)}</span>
              </KeyValue>
            ) : null}
            <KeyValue label={t("pay.network")}>{chainLabel(invoice.asset.chain)}</KeyValue>
            {invoice.paidMode ? (
              <KeyValue label={t("done.method")}>{invoice.paidMode === "wallet" ? t("done.methodWallet") : t("done.methodExchange")}</KeyValue>
            ) : null}
            {txHash && explorer ? (
              <KeyValue label={t("progress.tx")}>
                <ExternalLink href={explorer}>
                  <span className="mono">{shortHash(txHash, 6, 6)}</span>
                </ExternalLink>
              </KeyValue>
            ) : null}
            <KeyValue label={t("common.invoice")}>
              <span className="mono small">{invoice.id}</span>
            </KeyValue>
          </dl>
        </div>
      ) : null}

      <div className="result-actions">
        {redirect && settled ? (
          <RedirectCountdown href={redirect} merchant={merchant} delaySec={brand.checkout.redirectDelaySec} auto={sawPayment} />
        ) : redirect ? (
          <LinkButton href={redirect} variant="primary" size="lg" block>
            {view === "expired" ? t("done.backToStore") : t("done.returnTo", { merchant })}
          </LinkButton>
        ) : null}
        {view !== "expired" ? (
          <div className="result-actions-row">
            <Button variant="secondary" className="no-print" onClick={() => window.print()}>
              <IconReceipt size={16} />
              {t("done.download")}
            </Button>
            <Link className="btn btn-ghost btn-md" to={receiptHref}>
              <span className="btn-label">{settled ? t("done.viewReceipt") : t("pay.statusPage")}</span>
            </Link>
          </div>
        ) : (
          <Link className="btn btn-secondary btn-md btn-block" to={receiptHref}>
            <span className="btn-label">{t("pay.statusPage")}</span>
          </Link>
        )}
      </div>
      {settled ? <p className="muted small center no-print">{t("done.saveHint")}</p> : null}
    </section>
  );
}
